import React, { createContext, useContext, useRef } from 'react';

import { useSyncExternalStore } from 'use-sync-external-store/shim';

import type { RequireKeys } from '../../util/types';
import { useStableObject } from '../../util/use-stable-object';
import type {
	PostOfficeRouteConfig,
	PostOfficeRouteProviderProps,
	PostOfficeRouteStore,
	RouteChangeParams,
	RouteState,
	RouteStateDescription,
} from './types';

// Store

export const createPostOfficeRouteStore = ({
	routeSubscriber,
	initialUrl,
}: PostOfficeRouteConfig): PostOfficeRouteStore => {
	let state = initializeState({ initialUrl });

	const handleUpdate = (routeChange: RouteChangeParams) => {
		const next = routeChange.next?.toString();
		if (next === state.current && state.searchParams === routeChange.next?.searchParams) {
			return;
		}

		state = {
			previous: state.current,
			current: next,
			searchParams: routeChange.next?.searchParams,
		};
	};

	const subscribe = (next: () => void) =>
		routeSubscriber((routeChange) => {
			handleUpdate(routeChange);
			next();
		});

	const getSnapshot = () => {
		return state;
	};

	return {
		subscribe,
		getSnapshot,
	};
};

const usePostOfficeRouteStore = ({ subscribe, getSnapshot }: PostOfficeRouteStore) => {
	const route = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

	return useStableObject(route);
};

const initialState: RouteState = {
	previous: undefined,
	current: undefined,
	searchParams: undefined,
};

const initializeState = ({
	initialUrl,
}: Pick<PostOfficeRouteConfig, 'initialUrl'>): RouteState => ({
	...initialState,
	current: initialUrl ? initialUrl?.toString() : undefined,
	searchParams: initialUrl?.searchParams,
});

// Context

export const PostOfficeRouteContext: React.Context<RequireKeys<RouteStateDescription, never>> =
	createContext<RouteState>(initialState);

export const PostOfficeRouteProvider = ({
	children,
	...props
}: PostOfficeRouteProviderProps): JSX.Element => {
	const ref = useRef<PostOfficeRouteStore>();

	if (!ref.current) {
		ref.current = createPostOfficeRouteStore(props);
	}

	const route = usePostOfficeRouteStore(ref.current);

	return (
		<PostOfficeRouteContext.Provider value={route}>{children}</PostOfficeRouteContext.Provider>
	);
};

export const usePostOfficeRoute = (): RouteState => useContext(PostOfficeRouteContext);

//Route listener
